import React, { useId } from 'react';
import PropTypes from 'prop-types';
import styles from './MemorySymbolV1.css';

const mix = (from, to, t) => Math.round(from + (to - from) * t);

function renderStone(value, size) {
    const width = (28 + value * 1.1) * (size / 170);

    return (
        <i
            className={styles.stone}
            style={{ width: `${width.toFixed(1)}px`, height: `${(width * 0.8).toFixed(1)}px` }}
        />
    );
}

function renderShape(value, size, uid) {
    const points = 14;
    const radius = 62;
    const inner = radius * (0.99 - 0.55 * value / 100);
    const blur = (1 - value / 100) * 8;
    const t = value / 100;
    const polygon = [];

    for (let i = 0; i < points * 2; i++) {
        const angle = Math.PI * i / points - Math.PI / 2;
        const r = i % 2 ? inner : radius;

        polygon.push(`${(80 + r * Math.cos(angle)).toFixed(1)},${(80 + r * Math.sin(angle)).toFixed(1)}`);
    }

    const edge = `rgb(${mix(238, 77, t)},${mix(176, 78, t)},${mix(140, 107, t)})`;
    const core = `rgb(${mix(248, 156, t)},${mix(222, 59, t)},${mix(200, 87, t)})`;

    return (
        <svg
            width={size} height={size} viewBox='0 0 160 160'
            aria-hidden='true'
        >
            <defs>
                <filter
                    id={`f${uid}`} x='-30%' y='-30%'
                    width='160%' height='160%'
                >
                    <feGaussianBlur stdDeviation={blur.toFixed(1)} />
                </filter>
                <radialGradient id={`g${uid}`}>
                    <stop offset='0' stopColor={core} />
                    <stop offset='1' stopColor={edge} />
                </radialGradient>
            </defs>
            <polygon points={polygon.join(' ')} fill={`url(#g${uid})`} filter={`url(#f${uid})`} />
        </svg>
    );
}

function renderGlow(value, size, uid) {
    const t = value / 100;
    const core = `rgb(${mix(242, 208, t)},${mix(166, 16, t)},${mix(90, 46, t)})`;
    const edge = `rgb(${mix(246, 232, t)},${mix(196, 72, t)},${mix(140, 72, t)})`;
    const radius = 26 + value * 0.36;
    const blur = (1 - t) * 12 + 1.5;

    return (
        <svg
            width={size} height={size} viewBox='0 0 160 160'
            aria-hidden='true'
        >
            <defs>
                <filter
                    id={`f${uid}`} x='-60%' y='-60%'
                    width='220%' height='220%'
                >
                    <feGaussianBlur stdDeviation={blur.toFixed(1)} />
                </filter>
                <radialGradient id={`r${uid}`}>
                    <stop offset='0' stopColor={core} />
                    <stop offset={(0.45 + t * 0.35).toFixed(2)} stopColor={core} />
                    <stop offset='1' stopColor={edge} stopOpacity={(0.15 + t * 0.5).toFixed(2)} />
                </radialGradient>
            </defs>
            <circle
                cx='80' cy='80' r={radius.toFixed(1)}
                fill={`url(#r${uid})`} filter={`url(#f${uid})`}
            />
        </svg>
    );
}

/* A chapter's sign: a stone (weight), a drop that turns into a thorn (shape) or a glow (feeling) */
function MemorySymbolV1({ type, value, size }) {
    const uid = useId().replace(/:/g, '');

    if (type === 'stone') {
        return renderStone(value, size);
    }

    return type === 'shape' ? renderShape(value, size, uid) : renderGlow(value, size, uid);
}

MemorySymbolV1.propTypes = {
    type: PropTypes.oneOf(['stone', 'shape', 'glow']).isRequired,
    value: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired
};

export {
    MemorySymbolV1
};
